/**
 * Post-deposit balance sync: the React shell around {@link decideDepositPoll}.
 *
 * Watches the live main-wallet balance the caller already renders and re-reads `/wallet/list`
 * on a decaying schedule until it rises above the pre-deposit baseline. See `./deposit-sync`
 * for why this exists and for the schedule itself.
 */
import React from 'react';

import { useAppDispatch } from '@/store';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { decideDepositPoll } from './deposit-sync';

/** How long the "landed" confirmation stays on screen before the card goes back to normal. */
const CREDITED_NOTICE_MS = 5000;

export type DepositSync = {
  /** A read is in progress or scheduled. Drives the "Updating" affordance. */
  syncing: boolean;
  /** How much appeared, in major units, for the few seconds after it lands. */
  credited: number | null;
  /** Every attempt ran without the balance moving. */
  stalled: boolean;
  /** Start watching, from the balance as it stood before the deposit. */
  start: (baseline: number) => void;
  /** Manual "check again" after a stall, from the same baseline. */
  retry: () => void;
  /** Drop any in-flight watch (e.g. the active brand changed underneath us). */
  reset: () => void;
};

type Session = { baseline: number; attempt: number; active: boolean };

export function useDepositSync(balance: number | null): DepositSync {
  const dispatch = useAppDispatch();
  const [session, setSession] = React.useState<Session | null>(null);
  const [credited, setCredited] = React.useState<number | null>(null);
  const [stalled, setStalled] = React.useState(false);

  const start = React.useCallback((baseline: number) => {
    setCredited(null);
    setStalled(false);
    setSession({ baseline, attempt: 0, active: true });
  }, []);

  const retry = React.useCallback(() => {
    setStalled(false);
    setSession((prev) => (prev ? { ...prev, attempt: 0, active: true } : null));
  }, []);

  const reset = React.useCallback(() => {
    setSession(null);
    setCredited(null);
    setStalled(false);
  }, []);

  // Synchronises with the wallet API: re-reads it on a timer until the credit shows up.
  React.useEffect(() => {
    if (!session?.active) return undefined;

    const decision = decideDepositPoll({
      baseline: session.baseline,
      balance,
      attempt: session.attempt,
    });

    if (decision.type === 'landed') {
      setCredited(decision.credited);
      setSession((prev) => (prev ? { ...prev, active: false } : null));
      return undefined;
    }
    if (decision.type === 'exhausted') {
      setStalled(true);
      setSession((prev) => (prev ? { ...prev, active: false } : null));
      return undefined;
    }

    // The attempt counter is bumped as the read is ISSUED, not when it resolves, so attempts stay
    // spaced by the cadence rather than by however long the API takes. The cleanup cancels a
    // pending tick, so nothing fires after unmount.
    const timer = window.setTimeout(() => {
      void dispatch(fetchOwnerWallets());
      setSession((prev) => (prev ? { ...prev, attempt: prev.attempt + 1 } : null));
    }, decision.delayMs);
    return () => window.clearTimeout(timer);
  }, [session, balance, dispatch]);

  // The "+$X added" confirmation is a moment, not a permanent state.
  React.useEffect(() => {
    if (credited === null) return undefined;
    const timer = window.setTimeout(() => setCredited(null), CREDITED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [credited]);

  return { syncing: Boolean(session?.active), credited, stalled, start, retry, reset };
}
